/**
 * Curated catalog of messaging-related APIs grouped by KOL campaign progress.
 * Used by the agency API docs page (/agency/api-docs).
 */
import { MESSAGING_EVENTS, TEMPLATE_VARIABLES, type MessagingEventKey } from './messagingConstants';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type MessagingChannel = 'WHATSAPP' | 'EMAIL' | 'SMS' | 'IN_APP';

export type ApiEndpointDoc = {
  id: string;
  method: HttpMethod;
  path: string;
  purpose: string;
  /** Who typically calls this (agency staff, KOL, system). */
  actor?: string;
  /** MessagingEventKey(s) enqueued when this action succeeds. */
  eventKeys?: MessagingEventKey[];
  channels?: MessagingChannel[];
  notes?: string;
  sampleRequest?: unknown;
  sampleResponse?: unknown;
};

export type ApiDocsStage = {
  id: string;
  title: string;
  summary: string;
  /** Related invitation / script / submission status values. */
  statuses?: string[];
  endpoints: ApiEndpointDoc[];
};

export const DELIVERY_PIPELINE_STEPS = [
  'Agency (or KOL) action hits an API endpoint',
  'Backend calls notifyUsers() for the matching MessagingEventKey',
  'Side channels enqueue NotificationOutbox rows (PENDING)',
  'Outbox processor sends via WhatsApp Graph API and/or Plunk/SMTP',
  'Result lands in Messaging log as SENT or FAILED',
] as const;

export const SCRIPT_PIPELINE_PHASES = [
  'V1_BRIEF',
  'V2_AGENCY_SCRIPT',
  'V3_KOL_REVIEW',
  'V4_BRAND_REVIEW',
  'V5_VIDEO',
  'V6_AGENCY_QA',
  'V7_BRAND_FINAL',
] as const;

export const MESSAGING_API_STAGES: ApiDocsStage[] = [
  {
    id: 'invitation',
    title: '1. Invitation',
    summary:
      'Invite KOLs to a campaign, resend email, negotiate modifications, and approve for production. Automated WhatsApp/email uses INVITATION_SENT, BROADCAST_SENT, and INVITATION_RESPONDED templates.',
    statuses: ['PENDING', 'ACCEPTED', 'REJECTED', 'MODIFICATION_REQUESTED', 'WITHDRAWN'],
    endpoints: [
      {
        id: 'broadcast',
        method: 'POST',
        path: '/api/jobs/:jobId/broadcast',
        purpose: 'Create PENDING invitations for matched/selected KOLs and enqueue invite messages.',
        actor: 'Agency',
        eventKeys: ['INVITATION_SENT', 'BROADCAST_SENT'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
        notes:
          'Respects Job.messagingSettings channel toggles and templateRefs. Unregistered KOLs may receive a direct fallback email.',
        sampleRequest: {
          mode: 'manual',
          kolProfileIds: ['kol_profile_id'],
          multiplier: 1,
        },
        sampleResponse: {
          created: 12,
          invitationIds: ['inv_…'],
        },
      },
      {
        id: 'preview-matches',
        method: 'GET',
        path: '/api/jobs/:jobId/preview-matches',
        purpose: 'Preview scored KOL matches before broadcasting (no messages sent).',
        actor: 'Agency',
      },
      {
        id: 'broadcast-preview',
        method: 'POST',
        path: '/api/jobs/:jobId/broadcast/preview',
        purpose: 'Preview how many invitations a broadcast would create.',
        actor: 'Agency',
      },
      {
        id: 'resend-invite-email',
        method: 'POST',
        path: '/api/invitations/:id/resend-invite-email',
        purpose: 'Resend the invitation email via SMTP (bypasses messaging-template outbox).',
        actor: 'Agency',
        channels: ['EMAIL'],
        notes: 'Direct email path — does not use MessagingTemplate / NotificationOutbox for the body.',
      },
      {
        id: 'invitation-respond',
        method: 'POST',
        path: '/api/invitations/:id/respond',
        purpose: 'KOL accepts, rejects, or requests modification.',
        actor: 'KOL',
        eventKeys: ['INVITATION_RESPONDED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
        sampleRequest: {
          status: 'ACCEPTED',
          message: 'Happy to join',
        },
      },
      {
        id: 'respond-to-modification',
        method: 'POST',
        path: '/api/invitations/:id/respond-to-modification',
        purpose: 'Agency approves or rejects a KOL modification request.',
        actor: 'Agency',
        eventKeys: ['INVITATION_RESPONDED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
        sampleRequest: {
          decision: 'APPROVE_AND_ACCEPT',
          note: 'Updated rate accepted',
        },
      },
      {
        id: 'approve-production',
        method: 'POST',
        path: '/api/invitations/:id/approve-production',
        purpose: 'Mark invitation approved for production after shortlist.',
        actor: 'Agency / Brand',
        eventKeys: ['INVITATION_RESPONDED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
        notes: 'Template payload typically uses action = approved_for_production.',
      },
      {
        id: 'invitation-messages',
        method: 'POST',
        path: '/api/invitations/:id/messages',
        purpose: 'Post an in-app negotiation message on the invitation thread (not WhatsApp).',
        actor: 'Agency / KOL',
        channels: ['IN_APP'],
        notes: 'Use WhatsApp inbox for Meta Graph WhatsApp sends.',
      },
      {
        id: 'invitation-ai-draft',
        method: 'POST',
        path: '/api/invitations/:id/messages/ai-draft',
        purpose: 'AI-draft an in-app invitation message (copy only; does not send).',
        actor: 'Agency',
      },
    ],
  },
  {
    id: 'script',
    title: '2. Script',
    summary:
      'KOL submits scripts; brand/agency approve or request revision. Automated messages use SCRIPT_SUBMITTED, SCRIPT_APPROVED, and CONTENT_REVISED. Invitation.scriptPipelinePhase tracks V1_BRIEF → V7_BRAND_FINAL.',
    statuses: ['DRAFT', 'SUBMITTED_FOR_BRAND', 'APPROVED_BY_BRAND', 'REVISION_REQUESTED', 'WITHDRAWN'],
    endpoints: [
      {
        id: 'script-submit',
        method: 'POST',
        path: '/api/scripts/:id/submit',
        purpose: 'Submit script draft for brand review.',
        actor: 'KOL / Agency',
        eventKeys: ['SCRIPT_SUBMITTED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
      },
      {
        id: 'script-approve',
        method: 'POST',
        path: '/api/scripts/:id/approve',
        purpose: 'Approve the script (notifies KOL and agency).',
        actor: 'Brand / Agency',
        eventKeys: ['SCRIPT_APPROVED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
      },
      {
        id: 'script-revision',
        method: 'POST',
        path: '/api/scripts/:id/request-revision',
        purpose: 'Request script revisions with a comment.',
        actor: 'Brand / Agency',
        eventKeys: ['CONTENT_REVISED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
        sampleRequest: {
          comment: 'Please shorten the CTA',
        },
      },
    ],
  },
  {
    id: 'submission',
    title: '3. Submission',
    summary:
      'KOL uploads deliverables; agency/brand approve or request revision. Events: CONTENT_SUBMITTED, CONTENT_APPROVED, CONTENT_REVISED.',
    statuses: [
      'SUBMITTED',
      'PENDING_AGENCY_QA',
      'PENDING_BRAND_FINAL',
      'APPROVED',
      'REVISION_REQUESTED',
      'WITHDRAWN',
    ],
    endpoints: [
      {
        id: 'submission-create',
        method: 'POST',
        path: '/api/submissions',
        purpose: 'Create a content submission for an invitation.',
        actor: 'KOL',
        eventKeys: ['CONTENT_SUBMITTED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
        sampleRequest: {
          invitationId: 'inv_…',
          caption: 'Final cut',
          mediaUrls: ['https://…'],
        },
      },
      {
        id: 'submission-approve',
        method: 'POST',
        path: '/api/submissions/:id/approve',
        purpose: 'Approve submission (may include QA / brand-final variants on related routes).',
        actor: 'Agency / Brand',
        eventKeys: ['CONTENT_APPROVED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
      },
      {
        id: 'submission-revision',
        method: 'POST',
        path: '/api/submissions/:id/request-revision',
        purpose: 'Request video/content revisions.',
        actor: 'Agency / Brand',
        eventKeys: ['CONTENT_REVISED'],
        channels: ['WHATSAPP', 'EMAIL', 'IN_APP'],
        sampleRequest: {
          comment: 'Fix audio levels in first 5s',
        },
      },
    ],
  },
  {
    id: 'payment',
    title: '4. Payment',
    summary:
      'Invoice and payment reminder emails today. There is no MessagingEventKey for payments yet — WhatsApp provider integration should treat this as a planned gap.',
    statuses: ['PENDING', 'PAID', 'OVERDUE', 'CANCELLED'],
    endpoints: [
      {
        id: 'invoice-email',
        method: 'POST',
        path: '/api/jobs/:jobId/invoices/:invoiceId/send-email',
        purpose: 'Email invoice PDF to the brand (SMTP/Plunk path).',
        actor: 'Agency',
        channels: ['EMAIL'],
        notes: 'Not routed through MessagingTemplate event keys.',
      },
      {
        id: 'invoice-reminder',
        method: 'POST',
        path: '/api/jobs/:jobId/invoices/:invoiceId/send-reminder',
        purpose: 'Send payment reminder email for an invoice.',
        actor: 'Agency',
        channels: ['EMAIL'],
      },
      {
        id: 'milestone-remind',
        method: 'POST',
        path: '/api/jobs/:jobId/payment-milestones/:pmId/remind',
        purpose: 'In-app reminder for a payment milestone (not WhatsApp/outbox today).',
        actor: 'Agency',
        channels: ['IN_APP'],
        notes: 'Gap: no dedicated WhatsApp/email MessagingEventKey for KOL payment notices.',
      },
    ],
  },
  {
    id: 'manual-whatsapp',
    title: '5. Manual WhatsApp inbox',
    summary:
      '1:1 team inbox over Meta WhatsApp. Sends go directly via Graph API (text inside 24h window, or Meta-approved template names outside the window) — not via MessagingEventKey automation.',
    endpoints: [
      {
        id: 'threads-list',
        method: 'GET',
        path: '/api/whatsapp-inbox/threads',
        purpose: 'List inbox threads (filters: org, assignee, job, pagination).',
        actor: 'Agency',
      },
      {
        id: 'thread-detail',
        method: 'GET',
        path: '/api/whatsapp-inbox/threads/:threadId',
        purpose: 'Thread detail with messages and 24h window status.',
        actor: 'Agency',
      },
      {
        id: 'thread-start',
        method: 'POST',
        path: '/api/whatsapp-inbox/threads/start',
        purpose: 'Start or find a thread by phone number.',
        actor: 'Agency',
        sampleRequest: { phoneE164: '+60123456789', organizationId: 'org_…' },
      },
      {
        id: 'thread-send',
        method: 'POST',
        path: '/api/whatsapp-inbox/threads/:threadId/messages',
        purpose: 'Send free-text or Meta template message to the KOL.',
        actor: 'Agency (ADMIN/MANAGER/STAFF; OWNER/VIEWER read-only)',
        channels: ['WHATSAPP'],
        sampleRequest: {
          type: 'text',
          body: 'Hi — please submit your revised script today.',
        },
      },
      {
        id: 'thread-assign',
        method: 'POST',
        path: '/api/whatsapp-inbox/threads/:threadId/assign',
        purpose: 'Claim the thread for the current user.',
        actor: 'Agency',
      },
      {
        id: 'thread-release',
        method: 'POST',
        path: '/api/whatsapp-inbox/threads/:threadId/release',
        purpose: 'Release thread assignment.',
        actor: 'Agency',
      },
      {
        id: 'thread-link',
        method: 'PATCH',
        path: '/api/whatsapp-inbox/threads/:threadId/link',
        purpose: 'Link thread to kolProfileId / invitationId / jobId.',
        actor: 'Agency',
      },
      {
        id: 'window-status',
        method: 'GET',
        path: '/api/whatsapp-inbox/threads/:threadId/window-status',
        purpose: 'Check WhatsApp 24-hour customer care window.',
        actor: 'Agency',
      },
      {
        id: 'quick-replies',
        method: 'GET',
        path: '/api/whatsapp-inbox/quick-replies',
        purpose: 'List saved quick replies.',
        actor: 'Agency',
      },
      {
        id: 'quick-replies-create',
        method: 'POST',
        path: '/api/whatsapp-inbox/quick-replies',
        purpose: 'Create a quick reply.',
        actor: 'Agency',
      },
    ],
  },
  {
    id: 'config',
    title: '6. Configuration & tooling',
    summary:
      'Connect WABA, manage org messaging templates, monitor the outbox, create action deep links, and draft AI copy (AI does not send).',
    endpoints: [
      {
        id: 'waba-list',
        method: 'GET',
        path: '/api/waba/connections',
        purpose: 'List WhatsApp Business connections for agency orgs.',
        actor: 'Agency / Admin',
      },
      {
        id: 'waba-upsert',
        method: 'POST',
        path: '/api/waba/connections',
        purpose: 'Create or update org WABA connection (tokens, phoneNumberId, verify token).',
        actor: 'Agency admin',
      },
      {
        id: 'waba-test',
        method: 'POST',
        path: '/api/waba/connections/:organizationId/send-test',
        purpose: 'Enqueue and process a test WhatsApp message via the outbox.',
        actor: 'Agency admin',
        channels: ['WHATSAPP'],
      },
      {
        id: 'templates-list',
        method: 'GET',
        path: '/api/messaging-templates?organizationId=&includeVariants=',
        purpose: 'List org templates and optional named variants.',
        actor: 'Agency',
      },
      {
        id: 'templates-defaults',
        method: 'GET',
        path: '/api/messaging-templates/built-in-defaults',
        purpose: 'Built-in default copy per event × channel (no DB seed required).',
        actor: 'Agency',
      },
      {
        id: 'templates-preview',
        method: 'POST',
        path: '/api/messaging-templates/preview',
        purpose: 'Render a template with real or sample variables for a job/invitation.',
        actor: 'Agency',
      },
      {
        id: 'templates-upsert',
        method: 'PUT',
        path: '/api/messaging-templates/:organizationId',
        purpose: 'Bulk upsert org default templates.',
        actor: 'Agency',
      },
      {
        id: 'outbox-list',
        method: 'GET',
        path: '/api/notification-outbox',
        purpose: 'List delivery queue rows (status, channel, event, errors).',
        actor: 'Agency / Admin',
      },
      {
        id: 'outbox-process',
        method: 'POST',
        path: '/api/notification-outbox/process',
        purpose: 'Manually drain a pending outbox batch (platform admin).',
        actor: 'Platform ADMIN',
      },
      {
        id: 'action-links',
        method: 'POST',
        path: '/api/action-links',
        purpose: 'Create SSO deep links for KOL tasks (contract sign, script submit, etc.).',
        actor: 'Agency',
        notes: 'Embed {{actionLink}} in templates when the payload includes the link.',
      },
      {
        id: 'ai-generate-invitation',
        method: 'POST',
        path: '/api/ai-messages/generate-invitation',
        purpose: 'AI-generate invitation broadcast copy (does not send).',
        actor: 'Agency',
      },
      {
        id: 'ai-generate-follow-up',
        method: 'POST',
        path: '/api/ai-messages/generate-follow-up',
        purpose: 'AI-generate follow-up for a pending invitation.',
        actor: 'Agency',
      },
      {
        id: 'ai-generate-revision',
        method: 'POST',
        path: '/api/ai-messages/generate-script-revision',
        purpose: 'AI-generate script revision request copy.',
        actor: 'Agency / Brand',
      },
      {
        id: 'ai-generate-submission-revision',
        method: 'POST',
        path: '/api/ai-messages/generate-submission-revision',
        purpose: 'AI-generate submission revision request copy.',
        actor: 'Agency / Brand',
      },
      {
        id: 'ai-generate-approval',
        method: 'POST',
        path: '/api/ai-messages/generate-approval',
        purpose: 'AI-generate approval message copy.',
        actor: 'Agency / Brand',
      },
      {
        id: 'ai-deadline',
        method: 'POST',
        path: '/api/ai-messages/generate-deadline-reminder',
        purpose: 'AI-generate deadline reminder copy.',
        actor: 'Agency',
      },
    ],
  },
];

export { MESSAGING_EVENTS, TEMPLATE_VARIABLES };
export type { MessagingEventKey };

export function eventLabel(key: string): string {
  return MESSAGING_EVENTS.find((e) => e.key === key)?.label || key;
}

export function methodBadgeClass(method: HttpMethod): string {
  switch (method) {
    case 'GET':
      return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
    case 'POST':
      return 'bg-sky-500/15 text-sky-400 border-sky-500/30';
    case 'PUT':
      return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
    case 'PATCH':
      return 'bg-violet-500/15 text-violet-400 border-violet-500/30';
    case 'DELETE':
      return 'bg-rose-500/15 text-rose-400 border-rose-500/30';
    default:
      return 'bg-glass-3 text-glass-text-2 border-glass';
  }
}
